import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { GRID_MODAL } from "../../../constants/modals";
import { GRID_MODE_PANEL, GRID_MODE_MODAL } from "/shared/constants";
import { openModal } from "../../../actions/modals.actions";
import { withTranslation } from "react-i18next";
import { InvestmentsContext } from "../../../containers/investments/investment.container";
import AgGrid from "../../../dumb-components/shared/ag-grid/ag-grid";
import ExportExcelButton from "../../../dumb-components/investment/shareholders/export-excel-button";
import Moment from "../../../modules/moment.module";
import localeFormatNumber from "../../../modules/format-number";
import {
  NUMBER_FORMAT_CURRENCY,
  NUMBER_FORMAT_PERCENTAGE,
  NUMBER_FORMAT_INTEGER,
} from "/shared/constants";
import { getShareTypeLabel } from "../../../components/helpers/shares";
import { columnTypes } from "../../../dumb-components/shared/ag-grid/column-defs/column-defs";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import GridPanelRightButtons from "../../../dumb-components/shared/ag-grid/panel/grid-panel-right-buttons";
import Text from "../../../dumb-components/shared/text/text";
import { MyNumberOfShares } from "../../../dumb-components/shared/ag-grid/tooltips/share-types/MyNumberOfShares";

const formatCellCurrency = (value) =>
  typeof value === "number"
    ? localeFormatNumber(value, NUMBER_FORMAT_CURRENCY)
    : "-";

const formatCellInt = (value) =>
  typeof value === "number"
    ? localeFormatNumber(value, NUMBER_FORMAT_INTEGER)
    : "-";

const formatCellPercentage = (value) =>
  typeof value === "number"
    ? localeFormatNumber(value, NUMBER_FORMAT_PERCENTAGE)
    : "-";

const SharebookMyOwnershipGridContainer = ({ t, viaInvestmentId }) => {
  const { selectedInvestment } = useContext(InvestmentsContext);

  const gridRef = useRef();
  const [colDefs, setColDefs] = useState(null);
  const colTypes = columnTypes();
  const [rowData, setRowData] = useState(null);
  const companyName = useSelector((state) => {
    const capTableDetails = state.capTableDetails.toJS();
    return (
      capTableDetails.aliasOfTheInvestedCompany ||
      capTableDetails.combinedSingleInvestmentAndAllTransactions
        ?.investedInCompanyInformation?.name
    );
  });
  const selectedInvestmentJS = selectedInvestment?.toJS();
  const selectedInvestmentId = selectedInvestmentJS.id;
  const name = selectedInvestmentJS.investorInformation?.name;

  const gridOption = "sharebookMyOwnership";

  const isGridModalOpen = useSelector((state) => {
    const modals = state.modals.toJS();
    return modals.activeModal?.options === gridOption;
  });

  useEffect(() => {
    if (!selectedInvestment) {
      return null;
    }
    setColDefs(getColumnDefs());
    setRowData(getRowData());
  }, [selectedInvestment]);

  const dispatch = useDispatch();

  const getRowData = useCallback(() => {
    let transactions = formattedTransactions();
    const selectedInvestmentJS = selectedInvestment.toJS();
    const shareTypes = selectedInvestmentJS.details?.types || [];

    const percent = !viaInvestmentId
      ? selectedInvestmentJS.rootDetails?.ownershipPercentage ?? 100
      : 100;
    const quote = percent / 100;

    const numOfTotalShares = {
      id: "numOfTotalShares",
      title: t(
        "investments.sharebook.my_ownership.grid.row_title.shares_owned",
      ),
    };
    const ownershipPercentage = {
      id: "ownershipPercentage",
      title: t(
        "investments.sharebook.my_ownership.grid.row_title.percentage_of_company_owned",
      ),
    };
    const myPostMoney = {
      id: "myPostMoney",
      title: t(
        "investments.sharebook.my_ownership.grid.row_title.value_of_my_shares",
      ),
    };

    const transactionDetailsSectionId = t(
      "investments.general.shareholder_grid.header_name.transaction",
    );

    const transactionType = {
      id: "transactionType",
      title: t("own_ownership_registration.form_field.transaction_type"),
      section: transactionDetailsSectionId,
    };

    const transactionFromName = {
      title: t("from"),
      section: transactionDetailsSectionId,
    };

    const transactionToName = {
      title: t("to"),
      section: transactionDetailsSectionId,
    };

    let shareTypeData = shareTypes.map((shareType) => {
      return {
        id: "valueOfShare",
        title: t("value_of_share_N").replace(
          "{shareType}",
          getShareTypeLabel(shareType.type),
        ),
        type: shareType.type,
      };
    });

    const transactionNumOfSharesPerType = shareTypes.reduce(
      (prev, shareType) => ({
        ...prev,
        [shareType.type]: {
          id: "transactionNumOfShares",
          title: t("number_of_shares_N", {
            shareType: getShareTypeLabel(shareType.type),
          }),
          section: transactionDetailsSectionId,
        },
      }),
      {},
    );

    const transactionPriceOfSharesPerType = shareTypes.reduce(
      (prev, shareType) => ({
        ...prev,
        [shareType.type]: {
          id: "transactionPriceOfShares",
          title: t("price_of_shares_N", {
            shareType: getShareTypeLabel(shareType.type),
          }),
          section: transactionDetailsSectionId,
        },
      }),
      {},
    );

    transactions.forEach((transaction, index) => {
      const transactionJS = transaction.toJS?.() ?? transaction;
      const transactionDate = Moment(transactionJS.date).format("L");
      const dateIndexKey = transactionDate + "#" + index;

      numOfTotalShares[dateIndexKey] =
        transactionJS.balance.numOfTotalShares * quote;

      ownershipPercentage[dateIndexKey] =
        transactionJS.balance.ownershipPercentage * quote;

      myPostMoney[dateIndexKey] = transactionJS.balance.myPostMoney * quote;

      const transactionShareTypes = transactionJS.balance.types || [];

      if (!numOfTotalShares.types) {
        numOfTotalShares.types = {};
      }
      numOfTotalShares.types[dateIndexKey] = transactionShareTypes;
      numOfTotalShares.bodyTid =
        "investments.share_register.my_ownership_grid.row.title.tooltip.MyNumBerOfShares";

      shareTypes.forEach((shareType) => {
        // Calculate summarized value per share type
        const shareTypeInTransaction = transactionShareTypes.find(
          (transactionShareType) =>
            shareType.type === transactionShareType.type,
        );

        let latestPrice = shareTypeInTransaction
          ? shareTypeInTransaction.latestPrice
          : null;

        shareTypeData = shareTypeData.map((shareTypeDataItem) => {
          if (shareType.type === shareTypeDataItem.type) {
            shareTypeDataItem[dateIndexKey] = latestPrice;
          }
          return shareTypeDataItem;
        });

        switch (transactionJS.type) {
          case "TRANSFER-2": {
            // Calculate transaction amount of share per type
            transactionNumOfSharesPerType[shareType.type][dateIndexKey] =
              transactionJS?.handlerData?.sequences?.reduce((acc, seq) => {
                if (seq.shareType !== shareType.type) return acc;
                if (!seq.used) return acc;
                return acc + seq.used;
              }, 0);

            // Calculate transaction price of share per type
            transactionPriceOfSharesPerType[shareType.type][dateIndexKey] =
              transactionJS?.handlerData?.sequences?.find(
                (seq) => seq.shareType === shareType.type,
              )?.price;
            break;
          }
          case "EMISSION": {
            const handlerData = transactionJS?.handlerData;
            const shareholderHandlerData = handlerData?.shareholders?.find(
              (investor) => investor.investmentId === selectedInvestmentId,
            );

            const pricePerShare = handlerData?.types?.find(
              ({ type }) => type === shareType.type,
            )?.pricePerShare;

            transactionPriceOfSharesPerType[shareType.type][dateIndexKey] =
              pricePerShare;

            transactionNumOfSharesPerType[shareType.type][dateIndexKey] =
              shareholderHandlerData?.type === shareType.type
                ? shareholderHandlerData.numOfShares
                : undefined;
          }
        }
      });

      transactionType[dateIndexKey] = t(transactionJS.type);

      switch (transactionJS.type) {
        case "TRANSFER-2":
          transactionFromName[dateIndexKey] =
            transactionJS?.handlerData?.investmentNameFrom;

          transactionToName[dateIndexKey] =
            transactionJS?.handlerData?.investmentNameTo;
          break;
        case "EMISSION": {
          const investorParticipated =
            transactionJS?.handlerData?.shareholders?.some(
              (s) => s.investmentId === selectedInvestmentId,
            );

          transactionFromName[dateIndexKey] = companyName;
          if (investorParticipated) {
            transactionToName[dateIndexKey] = name;
          }
        }
      }
    });

    const outputRowData = [
      numOfTotalShares,
      ownershipPercentage,
      myPostMoney,
      ...shareTypeData,
      transactionType,
      transactionFromName,
      transactionToName,
      ...Object.values(transactionNumOfSharesPerType),
      ...Object.values(transactionPriceOfSharesPerType),
    ];

    return outputRowData;
  });

  const formattedTransactions = () => {
    const selectedInvestmentJS = selectedInvestment.toJS();
    let transactions = [...(selectedInvestmentJS.transactions || [])].reverse();

    if (!transactions || transactions.length === 0) {
      return [];
    }

    const fieldsToCompare = [
      ["balance", "numOfTotalShares"],
      ["balance", "ownershipPercentage"],
      ["balance", "myPostMoney"],
    ];

    let prevTransaction = null;

    transactions = transactions.filter((transaction) => {
      let isEqualToPrev = true;

      fieldsToCompare.forEach((fieldPath) => {
        const getNestedValue = (obj, path) => {
          return path.reduce((acc, key) => acc && acc[key], obj);
        };

        const prevValue =
          prevTransaction && getNestedValue(prevTransaction, fieldPath);
        const currentValue = getNestedValue(transaction, fieldPath);

        if (prevValue !== currentValue) {
          isEqualToPrev = false;
        }
      });
      prevTransaction = transaction;
      return !isEqualToPrev;
    });

    return transactions;
  };

  const getColumnDefs = () => {
    let transactions = formattedTransactions();
    let colDefs = [
      {
        field: "section",
        rowGroup: true,
        hide: true,
      },
      {
        field: "title",
        headerName: "",
        minWidth: 200,
        type: ["lockedFirstColumn", "widthDefault"],
      },
    ];

    transactions.forEach((transaction, index) => {
      const transactionDate = Moment(transaction.date).format("L");
      colDefs.push({
        field: transactionDate + "#" + index,
        headerName: transactionDate,
        minWidth: 200,
        type: "rightAligned",
        tooltipComponent: MyNumberOfShares,
        tooltipField: transactionDate + "#" + index,
        valueFormatter: (p) => {
          switch (p.data.id) {
            case "numOfTotalShares":
              return formatCellInt(p.value);
            case "ownershipPercentage":
              return formatCellPercentage(p.value);
            case "myPostMoney":
            case "valueOfShare":
            case "transactionPriceOfShares":
            case "transactionNumOfShares":
              return formatCellCurrency(p.value);
            default:
              return p.value ?? "-";
          }
        },
      });
    });

    return colDefs;
  };

  const exportAsExcel = useCallback(() => {
    gridRef.current.api.exportDataAsExcel();
  });

  const openGridModal = useCallback(() => {
    dispatch(openModal(GRID_MODAL, gridOption));
  });

  const modalLeftHeader = {
    tid: "investments.sharebook.grid_panel.my_ownership.title",
  };

  const onGridReady = (event) => {
    const allColumns = event.columnApi.getAllGridColumns();
    const lastColumn = allColumns[allColumns.length - 1];

    event.api.ensureColumnVisible(lastColumn.colId);
    event.columnApi.resetColumnState();
  };

  return (
    <Card>
      <CardHeader
        title={
          <Text
            tid={
              viaInvestmentId
                ? "my_company_ledger"
                : "investments.sharebook.grid_panel.my_ownership.title"
            }
            values={{ companyName: companyName, name: name }}
          />
        }
        action={
          <GridPanelRightButtons
            exportAsExcel={exportAsExcel}
            openGridModal={openGridModal}
            downloadExcelTid={
              "investments.sharebook.dropdown_item.download_excel_list.my_ownership"
            }
            noFilter={true}
          />
        }
      />
      <CardContent variant="panel">
        <AgGrid
          mode={isGridModalOpen ? GRID_MODE_MODAL : GRID_MODE_PANEL}
          isGridModalOpen={isGridModalOpen}
          rowData={rowData}
          columnDefs={colDefs}
          rightHeaderComponent={() => {
            return (
              <ExportExcelButton
                exportAsExcel={exportAsExcel}
                noFilter={true}
              />
            );
          }}
          modalLeftHeader={modalLeftHeader}
          gridRef={gridRef}
          columnTypes={colTypes}
          onGridReady={onGridReady}
          groupedGrid
          groupDisplayType="groupRows"
          groupRowRenderer="agGroupCellRenderer"
        />
      </CardContent>
    </Card>
  );
};

export default withTranslation()(SharebookMyOwnershipGridContainer);
